import { WidgetContentEntry, WidgetEntry, WidgetEntryType } from '@vestico/model';

export const DEFAULT_MAIN_BUTTON_INJECTION_ID = 'default-main-button';
export const MOBILE_SDK_MAIN_BUTTON_INJECTION_ID = 'mobile-sdk-main-button';

export const hasStaticEntryElement = (entry: WidgetEntry): boolean => {
  const { type, id, isThemeInserted, injectionScript } = entry;
  if (isThemeInserted) {
    return true;
  }
  switch (type) {
    case WidgetEntryType.imagePreview:
    case WidgetEntryType.link:
      return false;
    case WidgetEntryType.button:
      return id === DEFAULT_MAIN_BUTTON_INJECTION_ID || id === MOBILE_SDK_MAIN_BUTTON_INJECTION_ID;
    case WidgetEntryType.content:
      return isThemeInserted || injectionScript.trim().length === 0;
    default:
      throw new Error(`Unhandled entry type ${type}`);
  }
};

const addDefaultWidgetButton = () => {
  const formElementClassNames = ['product__form', 'product__form--add-to-cart', 'product-form', 'ProductForm', 'product-information-buttons'];
  let addToCartElements;
  for (const className of formElementClassNames) {
    addToCartElements = document.getElementsByClassName(className);
    if (addToCartElements.length > 0) {
      break;
    }
  }

  if (!addToCartElements || addToCartElements.length === 0) {
    console.error(`Could not find product form in document`);
    return null;
  }

  for (let index = 0; index < addToCartElements.length; index++) {
    const element = addToCartElements.item(index);
    if (element && element instanceof HTMLElement) {
      const widgetButtonContainer = document.createElement('div');
      widgetButtonContainer.setAttribute('style', 'margin-bottom:16px;width:100%;');
      element.insertBefore(widgetButtonContainer, element.childNodes[0]);
      return widgetButtonContainer;
    }
  }
  return null;
};

const getElementsForSelector = (selector: string) => {
  try {
    const elementCollection = document.querySelectorAll(selector);
    const elements: HTMLElement[] = [];
    for (let index = 0; index < elementCollection.length; index++) {
      const element = elementCollection.item(index);
      if (element && element instanceof HTMLElement) {
        elements.push(element);
      }
    }
    return elements;
  } catch (error) {
    console.error(error);
  }
  return [];
};

const getThemeEntries = (themeEntryID: string) => {
  const elements = getElementsForSelector(`[vw-theme-entry=${themeEntryID}]`).filter((element) => {
    return !element.getAttribute('vw-widget') && !element.getAttribute('vw-showcase');
  });
  return elements;
};

const getStaticButtonEntryElements = (entry: WidgetEntry, isSDK?: boolean) => {
  const { id, isThemeInserted } = entry;

  if (isThemeInserted) {
    return getThemeEntries('fit-match-button');
  }

  if (id === DEFAULT_MAIN_BUTTON_INJECTION_ID) {
    return addDefaultWidgetButton();
  }

  if (id === MOBILE_SDK_MAIN_BUTTON_INJECTION_ID && isSDK) {
    console.info('Inserting SDK match modal entry point');
    const container = document.getElementById('vestico-mobile-sdk-fit-match-modal');
    if (!container) {
      console.warn('Cannot find container div');
      return null;
    }
    const div = document.createElement('div');
    container.appendChild(div);
    return div;
  }

  return null;
};

const getStaticContentEntryElements = (entry: WidgetContentEntry, isSDK?: boolean): HTMLElement | HTMLElement[] | null => {
  // FIXME: THIS MIGHT REGRESS THE SDK - WE SHOULD USE NEW WIDGET ENTRY POINTS IN SDK INSTEAD
  if (isSDK && entry.location === 'pdps') {
    console.info('Inserting SDK fit carousel entry point');
    const container = document.getElementById('vestico-mobile-sdk-content-carousel');
    if (container) {
      const div = document.createElement('div');
      container.appendChild(div);
      return div;
    }
  }

  const { layoutSubType, id } = entry;

  const elements = getElementsForSelector(`[vw-widget=${id}]`);
  if (elements.length > 0) {
    return elements;
  }

  // We always insert the popup element at the bottom of the page
  if (layoutSubType === 'popup') {
    const container = document.createElement('div');
    container.setAttribute('vw-widget', id);
    container.setAttribute('vw-theme-entry', 'widget');
    document.body.appendChild(container);
    return [container];
  }

  // LEGACY - DEPRECATED - REMOVE ONCE ALL CLIENTS HAVE BEEN MIGRATED TO NEW WIDGET ENTRIES
  const legacyShowcaseElements = getElementsForSelector(`[vw-showcase=${id}]`);
  if (legacyShowcaseElements.length > 0 || id.includes('showcase')) {
    return legacyShowcaseElements;
  }

  // LEGACY - DEPRECATED - REMOVE ONCE ALL CLIENTS HAVE BEEN MIGRATED TO NEW WIDGET ENTRIES
  const legacyEntries = getElementsForSelector('[vw-theme-entry]').filter((element) => {
    const themeEntryID = element.getAttribute('vw-theme-entry');

    return (
      (themeEntryID === 'stories' && layoutSubType === 'stories' && id.includes('stories')) ||
      (themeEntryID === 'content-carousel' &&
        id.includes('content-carousel') &&
        ['grid', 'singleRow', 'singleRowFixed', 'singleRowFreeScroll'].includes(layoutSubType))
    );
  });
  return legacyEntries;
};

export const getStaticEntryElements = (entry: WidgetEntry, isSDK?: boolean): HTMLElement[] | null => {
  const { type } = entry;

  const getElementsForType = () => {
    switch (type) {
      case WidgetEntryType.imagePreview:
      case WidgetEntryType.link:
        return null;
      case WidgetEntryType.button:
        return getStaticButtonEntryElements(entry, isSDK);
      case WidgetEntryType.content:
        return getStaticContentEntryElements(entry as WidgetContentEntry, isSDK);

      default:
        throw new Error(`Unhandled entry type ${type}`);
    }
  };
  const elements = getElementsForType();
  if (!elements) {
    return null;
  }
  return Array.isArray(elements) ? elements : [elements];
};
